// NewKBRecordCallModal-aiFeatures.js
import aiDataService from '../../../../services/ai-data.service'; // Ensure this path is correct
import api from '../../../../services/api'; // Ensure this path is correct
import { saveExtractionResults as saveExtractionResultsUtil } from '../../../../utils/aiExtraction'; // Ensure this path is correct

/**
 * Extracts information from call notes using AI.
 * @param {string} callNotes - The call notes to process.
 * @param {object} lead - The lead object.
 * @param {function} toast - The Chakra UI toast function.
 * @param {function} setIsExtractionModalOpenState - State setter for AI extraction modal visibility.
 * @param {function} setExtractedDataState - State setter for the extracted AI data.
 * @param {function} setIsExtractingState - State setter for AI extraction loading state.
 * @param {boolean} isMainModalOpen - Current open state of the main NewKBRecordCallModal.
 * @returns {Promise<object|null>} Extracted data or null on failure/no notes.
 */
export const extractInformationFromNotes = async (
  callNotes,
  lead,
  toast,
  setIsExtractionModalOpenState,
  setExtractedDataState,
  setIsExtractingState,
  isMainModalOpen
) => {
  if (!callNotes.trim()) {
    toast({
      title: 'No call notes',
      description: 'Please enter call notes to extract information.',
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });
    return null;
  }

  try {
    toast({
      title: 'AI Processing',
      description: 'Analyzing call notes with AI (this may take a few seconds)...',
      status: 'info',
      duration: 10000, // Keep it open longer as AI can take time
      isClosable: true,
    });

    setIsExtractingState(true);
    const leadId = lead?.leadId || lead?._id;
    console.log('[AI Features] Extracting info from call notes for lead:', leadId);

    const response = await aiDataService.processCallNotes(callNotes, leadId);
    console.log('[AI Features] Extracted data response:', response);

    if (response) {
      saveExtractionResultsUtil(response, leadId); // Save to localStorage via utility
      setExtractedDataState(response);

      if (isMainModalOpen) { // Only open extraction modal if main modal is (or will be) open
        setIsExtractionModalOpenState(true);
      } else {
        // If main modal closed, perhaps because call log saved and closed it,
        // rely on AIExtractionMonitor or a similar mechanism to show results.
        toast({
          title: 'AI Extraction Complete',
          description: 'Information has been extracted. Review it if needed.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }
      return response;
    } else {
      // Handle cases where AI service returns a non-error but empty/invalid response
      console.warn('[AI Features] AI extraction returned no data or an unexpected format.');
      toast({
          title: 'AI Extraction Note',
          description: 'AI processing completed but no specific information was extracted.',
          status: 'info',
          duration: 5000,
          isClosable: true,
      });
      return null; // Or response, depending on how you want to handle "empty" successful extractions
    }
  } catch (error) {
    console.error('[AI Features] Error extracting info:', error);
    toast({
      title: 'AI Extraction Failed',
      description: error.message || 'Failed to extract information from call notes.',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
    return null;
  } finally {
    setIsExtractingState(false);
  }
};

/**
 * Updates the lead with data extracted by AI.
 * @param {object} updateData - The data to update the lead with.
 * @param {object} lead - The lead object.
 * @param {function} onSuccessMainCallback - The main onSuccess callback from NewKBRecordCallModal.
 * @param {function} toast - The Chakra UI toast function.
 * @returns {Promise<object|null>} Updated lead data or null on failure.
 */
export const applyExtractedDataToLead = async (updateData, lead, onSuccessMainCallback, toast) => {
  if (!updateData || Object.keys(updateData).length === 0) {
    toast({
        title: 'No AI Data',
        description: 'No data provided to update the lead.',
        status: 'info',
        duration: 3000,
        isClosable: true,
    });
    return null;
  }

  if (!lead?.leadId && !lead?._id) {
    toast({
        title: 'Lead not found',
        description: 'Cannot update lead without a valid lead ID.',
        status: 'error',
        duration: 3000,
        isClosable: true,
    });
    return null;
  }

  try {
    const leadId = lead.leadId || lead._id;
    console.log('[AI Features] Updating lead', leadId, 'with data:', updateData);

    const payload = { ...updateData }; // Ensure only relevant fields are sent
    const response = await api.put(`/api/leads/${leadId}`, payload);

    toast({ // Add toast for successful update
        title: 'Lead Updated with AI Data',
        status: 'success',
        duration: 3000,
        isClosable: true,
    });

    if (typeof onSuccessMainCallback === 'function' && response.data) {
      onSuccessMainCallback(response.data); // Propagate success with updated lead
    }
    return response.data;
  } catch (error) {
    console.error('[AI Features] Error updating lead with extracted data:', error);
    toast({
        title: 'AI Update Failed',
        description: error.response?.data?.message || error.message || 'Failed to update lead with AI data.',
        status: 'error',
        duration: 5000,
        isClosable: true,
    });
    return null;
  }
};